import { computed, watch, ref } from '@nuxtjs/composition-api';
import { STOCK_STATUS } from '@/constants/StockStatus'
import useVariantFeatures from '~/composable/useVariantFeatures';

// eslint-disable-next-line max-params
export default function useVariantDetails (productVariants, chosenColor, chosenStorage, priceIndexRequired = false, getPricePerSku, productSlug = undefined, getInventory, categorySlug, appContext) {
  const chosenVariantPriceMatrix = ref(null)
  const masterProduct = computed(() => productVariants.value?.products.find(product => product.isMaster));
  const masterProductSlug = computed(() => masterProduct?.value?.slug)
  const isVariantSlug = () => { return productSlug !== undefined && productSlug !== masterProductSlug.value }
  const selectingDefaultVariant = ref(false)
  const isMobilePhonesCategory = () => (categorySlug || '').toLowerCase() === 'mobiltelefoner'
  const chosenVariant = computed(() => {
    if (chosenColor.value === null && chosenStorage.value === null) {
      if (isVariantSlug()) {
        return productVariants.value?.products.find(product => product.variantKey.toLowerCase() === productSlug?.toLowerCase())
      }
      return productVariants.value?.products.find(product => product.isMaster)
    } else {
      const chosen = productVariants.value?.products.find(product => product.productColorHex === chosenColor.value &&
        product.storage === chosenStorage.value)
      if (chosen === undefined) {
        return productVariants.value?.products.find(product => product.storage === chosenStorage.value)
      }
      return chosen
    }
  });

  const activeColor = computed(() => chosenVariant.value?.productColorHex)
  const activeColorName = computed(() => chosenVariant.value?.colorDescription)
  const activeStorage = computed(() => chosenVariant.value?.storage)
  const productColorVariants = computed(() => {
    const filteredVariantsByStorage = productVariants.value?.products.filter(product => product.storage === activeStorage.value)
    const colors = []
    if (filteredVariantsByStorage !== undefined) {
      filteredVariantsByStorage.forEach(variant => {
        colors.push(variant.productColorHex)
      })
    }
    return colors
  })
  const productStorageVariants = computed(() => {
    if (productVariants.value?.storageOptions[0] !== null) {
      const unitMultiplier = {
        TB: 1000,
        GB: 1
      }
      const parseValue = x => {
        const arr = x.split(' ')
        return (parseInt(arr[0]) * (unitMultiplier[arr[1]] || 1))
      }
      return productVariants.value?.storageOptions?.sort((a, b) => parseValue(a) - parseValue(b))
    }
    return null
  })
  const priceIndexPerStorage = computed(() => { // price list of per storage and activeColor Combination
    return productStorageVariants.value?.map((storageOption) => {
      let variantPrice = null
      const variant = productVariants.value?.products.find(product => product.productColorHex === activeColor.value &&
        product.storage === storageOption)
      if (variant !== undefined) {
        variantPrice = variant.basePrice
      }
      return { storage: storageOption, price: variantPrice }
    })
  })
  const chosenVariantSku = computed(() => chosenVariant.value?.sku)
  const getVariantPrice = () => {
    if (priceIndexRequired && chosenVariantSku.value !== undefined) {
      getPricePerSku(chosenVariantSku.value, categorySlug).then(res => {
        chosenVariantPriceMatrix.value = res.data
      })
    }
  }
  getVariantPrice()
  const skuInventoryStatus = ref('')
  const purchaseCategory = ref('')
  const expectedDeliveryDate = ref()
  // eslint-disable-next-line complexity
  const getInventoryStatus = async() => {
    if (!getInventory) { return; }
    if (chosenVariantSku.value !== undefined) {
      const inventory = await getInventory(chosenVariantSku.value)
      setInventoryStatus(inventory)

      // Prefer in-stock variant for mobile phones when landing on master without explicit variant selection
      if (
        isMobilePhonesCategory() &&
        !isVariantSlug() &&
        chosenColor.value === null &&
        chosenStorage.value === null &&
        chosenVariant.value?.isMaster &&
        skuInventoryStatus.value !== STOCK_STATUS.IN_STOCK &&
        !selectingDefaultVariant.value
      ) {
        selectingDefaultVariant.value = true
        const inStockVariant = await findFirstInStockVariant()
        if (inStockVariant) {
          chosenColor.value = inStockVariant.productColorHex || null
          chosenStorage.value = inStockVariant.storage || null
        }
        selectingDefaultVariant.value = false
      }
    }
  }
  const setInventoryStatus = (inventory) => {
    // if (inventory?.purchaseCategory === STOCK_STATUS.NORMAL_ORDER) {
    //   skuInventoryStatus.value = inventory?.quantityOnStock <= 0 ? STOCK_STATUS.OUT_OF_STOCK : STOCK_STATUS.IN_STOCK
    // } else {
    //   skuInventoryStatus.value = inventory?.purchaseCategory
    // }
    skuInventoryStatus.value = inventory?.stockStatus;
    purchaseCategory.value = inventory?.purchaseCategory;
    expectedDeliveryDate.value = inventory?.expectedDeliveryDate
  }
  getInventoryStatus()
  watch(chosenVariantSku, () => {
    getVariantPrice()
    getInventoryStatus()
  })
  const findFirstInStockVariant = async () => {
    if (!getInventory) { return null }
    const variants = productVariants.value?.products?.filter(p => !p.isMaster) || []
    for (const variant of variants) {
      const inv = await getInventory(variant.sku)
      if (inv?.stockStatus === STOCK_STATUS.IN_STOCK) {
        return variant
      }
    }
    return null
  }
  const masterVariantPricingDetails = computed(() => productVariants.value?.masterVariantPricingDetails)
  const productName = computed(() => productVariants.value?.productFamilyName)
  const productAttributes = computed(() => chosenVariant.value?.productAttributes)
  const { productSpecList, featureList: getFeatureList, getAttributeValue, getAttributeValueIfPresent } = useVariantFeatures(productAttributes.value, appContext)

  // Create a reactive featureList that updates when productAttributes change
  const featureList = ref([])

  const updateFeatureList = async () => {
    if (productAttributes.value) {
      featureList.value = await getFeatureList()
    }
  }

  // Watch for changes in productAttributes and update featureList
  watch(productAttributes, updateFeatureList, { immediate: true })
  const productBrand = computed(() => productVariants.value?.brandName)

  const hasAnyValidFeature = computed(() => {
    const checks = [
      () => getAttributeValue('ScreenResolution'),
      () => getAttributeValue('FrontCameraResolution'),
      () => getAttributeValue('Weight'),
      () => getAttributeValueIfPresent('charger'),
      () => getAttributeValueIfPresent('watt'),
      () => getAttributeValueIfPresent('usb-pd'),
      () => getAttributeValueIfPresent('energy-efficiency-class')
    ]
    return checks.some(fn => fn() !== undefined)
  })

  return {
    stockCheck: computed(() => skuInventoryStatus.value === STOCK_STATUS.IN_STOCK),
    skuInventoryStatus: computed(() => skuInventoryStatus.value),
    purchaseCategory: computed(() => purchaseCategory.value),
    expectedDeliveryDate: computed(() => expectedDeliveryDate.value),
    masterProduct,
    chosenVariant,
    masterProductPrice: computed(() => masterProduct.value?.prices[0].value.centAmount),
    // productPrice: computed(() => masterProduct.value?.prices[0].value.centAmount),
    productPrice: computed(() => chosenVariant.value?.prices[0].value.centAmount),
    productBrand,
    productName,
    productFamilyColorVariants: computed(() => productVariants.value?.colorOptionsHexCodes), // complete list of all colors for a product
    masterProductImages: computed(() => masterProduct.value?.productContent?.productImages), // product images for master variant
    masterProductLabels: computed(() => masterProduct.value?.productContent?.productLabelList),
    masterProductTimer: computed(() => masterProduct.value?.productContent?.productTimer),
    masterProductSlug,
    productColorVariants,
    productLabels: computed(() => chosenVariant.value?.productContent?.productLabelList),
    productTimer: computed(() => chosenVariant.value?.productContent?.productTimer),
    productOfferMessage: computed(() => chosenVariant.value?.productContent?.productOfferMessage),
    productImages: computed(() => chosenVariant.value?.productContent?.productImages),
    productInformation: computed(() => chosenVariant.value?.productContent?.productInfo),
    campaign: computed(() => chosenVariant.value?.productContent?.campaign),
    productPromotionalImage: computed(() => chosenVariant.value?.productContent?.productPromotionalImage),
    productDetailFaq: computed(() => {
      const detailsFaq = chosenVariant.value?.productContent?.tlmProductFaQs?.map((val) => {
        return {
          heading: val.question,
          content: val.answer,
          specificationList: null
        }
      }) || []
      if (productSpecList.value.length > 0) {
        const productSpecifications = {
          heading: 'Specifikationer',
          specificationList: productSpecList.value
        }
        detailsFaq.unshift(productSpecifications)
      }
      return detailsFaq
    }),
    featureList,
    hasValidFeatureValue: hasAnyValidFeature.value,
    uniqueSellingPoint: computed(() => chosenVariant.value?.productContent?.uniqueSellingPoints),
    productArticles: computed(() => chosenVariant.value?.productContent?.articles),
    productDescription: computed(() => chosenVariant.value?.productContent?.productDescription),
    productAttributes,
    products: computed(() => productVariants.value?.products),
    multiPropProdCategory: computed(() => productVariants.value?.storageOptions[0] !== null ||
      chosenVariantPriceMatrix?.value?.subscriptionBundleOptions?.length > 0 ||
      chosenVariantPriceMatrix?.value?.cashOption?.installmentOptions?.length > 0),
    masterVariantPricingDetails,
    chosenVariantCashPrice: computed(() => chosenVariant.value?.cashPrice),
    chosenVariantBasePrice: computed(() => chosenVariant.value?.basePrice),
    defaultChannel: computed(() => masterVariantPricingDetails.value?.bundleID),
    defaultInstallmentPeriod: computed(() => chosenVariant.value?.defaultInstallmentPeriod),
    metaDescription: computed(() => {
      if (productVariants.value?.metaDescription === '') {
        return isVariantSlug()
          ? productBrand.value + ' ' + productName.value + ' ' + activeColorName.value + ' ' + activeStorage.value + productVariants.value?.metaDescription
          : productVariants.value?.metaDescription
      }
      return productVariants.value?.metaDescription
    }),
    metaTitle: computed(() => {
      if (productVariants.value?.metaTitle === '') {
        return isVariantSlug() ? productBrand.value + ' ' + productName.value + ' ' + activeColorName.value + ' ' + activeStorage.value + ' » Køb allerede i dag! | Telmore.dk' : productBrand.value + ' ' + productName.value + ' » Køb allerede i dag! | Telmore.dk'
      }
      return productVariants.value?.metaTitle
    }),
    masterProductCategories: computed(() => masterProduct.value?.categoryIDs),
    activeColor,
    activeColorName,
    activeStorage,
    priceIndexPerStorage,
    productStorageVariants,
    chosenVariantPriceMatrix,
    chosenVariantSku,
    variantKey: computed(() => chosenVariant.value?.variantKey)
  }
}
